import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Bug, EyeOff, Eye, Send, Info, Paperclip, Plus, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { getConsoleErrorBuffer } from '../hooks/useConsoleErrorBuffer';
import { formatVersion, getBuildNumber } from '../version';

export default function QuickBugModal({ open, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [includeContext, setIncludeContext] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{ file, preview }]
  const fileInputRef = useRef(null);

  const isImageFile = (f) => f.type && f.type.startsWith('image/');

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const MAX_BYTES = 10 * 1024 * 1024;
    setAttachments(prev => {
      const next = [...prev];
      for (const file of incoming) {
        if (file.size > MAX_BYTES) {
          toast.error(t('feedback.form.attachmentTooLarge', { name: file.name }));
          continue;
        }
        if (next.length >= 10) {
          toast.error(t('feedback.form.attachmentLimit'));
          break;
        }
        next.push({ file, preview: isImageFile(file) ? URL.createObjectURL(file) : null });
      }
      return next;
    });
  }, [t]);

  const removeAttachment = (idx) => {
    setAttachments(prev => {
      const item = prev[idx];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles = [];
    for (const it of items) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) imageFiles.push(f);
      }
    }
    if (imageFiles.length) {
      e.preventDefault();
      addFiles(imageFiles);
      toast.success(t('feedback.form.attachmentPasted'));
    }
  }, [addFiles, t]);

  const context = useMemo(() => {
    if (!open) return null;
    return {
      url: window.location.href,
      pathname: window.location.pathname,
      userAgent: navigator.userAgent,
      version: `${formatVersion()} · build ${getBuildNumber()}`,
      commit: process.env.REACT_APP_GIT_COMMIT || 'unknown',
      commitTime: process.env.REACT_APP_GIT_TIME || '',
      errors: getConsoleErrorBuffer(),
      viewport: `${window.innerWidth}×${window.innerHeight}`,
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setSeverity('medium');
      setIsAnonymous(false);
      setIncludeContext(true);
      setAttachments(prev => {
        prev.forEach(a => { if (a.preview) URL.revokeObjectURL(a.preview); });
        return [];
      });
    }
  }, [open]);

  // Cleanup object URLs on unmount
  useEffect(() => () => {
    attachments.forEach(a => { if (a.preview) URL.revokeObjectURL(a.preview); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open || !context) return null;

  const buildContextText = () => {
    const lines = [
      '',
      '---',
      '**Contexto capturado automáticamente**',
      `- URL: ${context.url}`,
      `- Viewport: ${context.viewport}`,
      `- Commit: ${context.commit}${context.commitTime ? ` (${context.commitTime})` : ''}`,
      `- UA: ${context.userAgent}`,
    ];
    if (context.errors.length > 0) {
      lines.push('', '**Últimos errores en consola:**');
      context.errors.forEach(e => {
        lines.push(`- \`${e.time}\` ${e.msg}`);
      });
    }
    return lines.join('\n');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.error(t('feedback.errorRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const body = includeContext
        ? description.trim() + '\n' + buildContextText()
        : description.trim();
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('description', body);
      fd.append('type', 'bug');
      fd.append('severity', severity);
      fd.append('isAnonymous', String(!!isAnonymous));
      for (const a of attachments) fd.append('attachments', a.file);
      const { data } = await api.post('/feedback', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('feedback.submitted'));
      onClose();
      if (data?.id) navigate(`/feedback?id=${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <div
      onMouseDown={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px',
        overflowY: 'auto',
      }}
    >
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        onPaste={handlePaste}
        style={{
          background: 'var(--c-surface)', borderRadius: 10, maxWidth: 560, width: '100%',
          border: '1px solid var(--c-border)', boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column', maxHeight: '90vh',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 14, borderBottom: '1px solid var(--c-border)' }}>
          <Bug size={18} style={{ color: '#ef4444' }} />
          <span style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>{t('feedback.quickBug.title')}</span>
          <span style={{ fontSize: 10, color: 'var(--c-text-muted)', fontFamily: 'monospace', padding: '2px 6px', borderRadius: 4, background: 'var(--c-surface-2)' }}>
            Ctrl+Shift+B
          </span>
          <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--c-text-muted)' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            autoFocus
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder={t('feedback.quickBug.titlePlaceholder')}
            maxLength={255}
            required
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 6, fontSize: 14,
              border: '1px solid var(--c-border)', background: 'var(--c-surface-2)',
              color: 'var(--c-text)', boxSizing: 'border-box',
            }}
          />

          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('feedback.quickBug.descriptionPlaceholder')}
            rows={5}
            required
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 6, fontSize: 14,
              border: '1px solid var(--c-border)', background: 'var(--c-surface-2)',
              color: 'var(--c-text)', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit',
            }}
          />

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>{t('feedback.form.severity')}:</label>
            <select
              value={severity}
              onChange={e => setSeverity(e.target.value)}
              style={{
                padding: '6px 10px', borderRadius: 6, fontSize: 13,
                border: '1px solid var(--c-border)', background: 'var(--c-surface-2)', color: 'var(--c-text)',
              }}
            >
              <option value="low">{t('feedback.severity.low')}</option>
              <option value="medium">{t('feedback.severity.medium')}</option>
              <option value="high">{t('feedback.severity.high')}</option>
              <option value="critical">{t('feedback.severity.critical')}</option>
            </select>
          </div>

          {/* Context preview */}
          <div style={{
            background: 'var(--c-surface-2)', borderRadius: 6, border: '1px solid var(--c-border)',
            padding: 10, fontSize: 11, fontFamily: 'monospace', color: 'var(--c-text-muted)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <Info size={12} />
              <span style={{ fontSize: 12, fontWeight: 500 }}>{t('feedback.quickBug.contextCaptured')}</span>
              <label style={{ marginLeft: 'auto', cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={includeContext}
                  onChange={e => setIncludeContext(e.target.checked)}
                />
                {t('feedback.quickBug.includeContext')}
              </label>
            </div>
            <div style={{ lineHeight: 1.5 }}>
              <div>URL: {context.pathname}</div>
              <div>Commit: {context.commit.slice(0, 7)} · {context.viewport}</div>
              {context.errors.length > 0 ? (
                <div style={{ marginTop: 4, color: '#ef4444' }}>
                  {t('feedback.quickBug.errorsCount', { count: context.errors.length })}
                </div>
              ) : (
                <div style={{ marginTop: 4, color: '#10b981' }}>
                  {t('feedback.quickBug.noErrors')}
                </div>
              )}
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={e => setIsAnonymous(e.target.checked)}
            />
            {isAnonymous ? <EyeOff size={14} /> : <Eye size={14} />}
            <span>{t('feedback.form.anonymous')}</span>
          </label>

          {/* Attachments */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 12, color: 'var(--c-text-muted)' }}>
              <Paperclip size={13} />
              <span>{t('feedback.form.attachments')}</span>
              <span style={{ marginLeft: 'auto', fontSize: 11 }}>{/* En un celular no hay Ctrl+V: el tip se cambia por el que sí sirve (Thiago, 30-sep). */}
              {t(typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches ? 'feedback.form.pasteHintTouch' : 'feedback.form.pasteHint')}</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.txt,.log,.json,.csv"
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                border: '1px dashed var(--c-border)', background: 'var(--c-surface-2)', color: 'var(--c-text)',
              }}
            >
              <Plus size={13} />
              {t('feedback.form.addAttachment')}
            </button>
            {attachments.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {attachments.map((a, idx) => (
                  <div key={idx} style={{
                    position: 'relative', width: 72, height: 72, borderRadius: 6,
                    border: '1px solid var(--c-border)', background: 'var(--c-surface-2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                  }}>
                    {a.preview ? (
                      <img src={a.preview} alt={a.file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: 2, fontSize: 9, color: 'var(--c-text-muted)', textAlign: 'center' }}>
                        <FileText size={18} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 60 }}>{a.file.name}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttachment(idx)}
                      style={{
                        position: 'absolute', top: 1, right: 1, border: 'none',
                        background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: '50%',
                        width: 18, height: 18, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: 12, borderTop: '1px solid var(--c-border)' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px', borderRadius: 6, border: '1px solid var(--c-border)',
              background: 'var(--c-surface-2)', color: 'var(--c-text)', cursor: 'pointer', fontSize: 13,
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !title.trim() || !description.trim()}
            style={{
              padding: '8px 16px', borderRadius: 6, border: 'none',
              background: '#ef4444', color: '#fff', cursor: submitting ? 'wait' : 'pointer',
              fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6,
              opacity: (submitting || !title.trim() || !description.trim()) ? 0.5 : 1,
            }}
          >
            <Send size={13} />
            {submitting ? t('common.saving') : t('feedback.quickBug.report')}
          </button>
        </div>
      </form>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
